import { Alert } from '@cmsgov/ds-medicare-gov';
import { MouseEventHandler, PropsWithChildren, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import Button from '../../common/components/button';
import Div from '../../common/components/div';
import H1 from '../../common/components/h1';
import StackingButtons from '../../common/components/StackingButtons';
import StepHeader from '../../common/components/StepHeader';
import TranslatedSpan from '../../common/components/TranslatedSpan';
import capitalizeFirstLetter from '../../common/functions/capitalizeFirstLetter';
import translate from '../../common/functions/translate';
import { useConversion, useFormStart, usePageViewWithSteps } from '../../common/hooks/use.analytics';
import { usePageTitle } from '../../common/hooks/usePageTitle';
import { DateObject } from '../../common/objects/models/date.field.value';
import { AllFieldsRequired } from '../../common/components/AllFieldsRequired';
import { EnterYourInfo } from './EnterYourInfo';
import { AnswerSecretQuestion } from './AnswerSecretQuestion';
import { CreateNewPassword } from './CreateNewPassword';

interface StepProps {
    title?: string
    stepNum: number
    stepTitle?: string
    combinedWithUsername?: boolean
    allFieldsRequired?: boolean
    showAlert?: boolean
    mfaPasswordReset?: true
}

export const Step = (props: PropsWithChildren<StepProps>) => {
    const {title, stepNum, stepTitle, combinedWithUsername, allFieldsRequired, showAlert, mfaPasswordReset} = props;
    const step = combinedWithUsername ? stepNum + 1 : stepNum;
    const numSteps = combinedWithUsername ? 4 : 3;
    const pageTitle = combinedWithUsername ? translate('forgot_username_and_password') : title;
    const showStepNum = (step && !mfaPasswordReset) && step <= numSteps;
    
    const [headingRef, setHeadingRef] = useState<HTMLHeadingElement | null>(null);
    const header = pageTitle || capitalizeFirstLetter(translate('reset_password').toLowerCase());
    usePageTitle(header);
    usePageViewWithSteps(header, step, stepTitle || title || '');

    // When refactoring the reset password flow into multiple components this should no longer be necessary
    useEffect(() => {
        setTimeout(() => {
            headingRef?.focus();
        }, 10);
    }, [stepNum, headingRef]);

    return <>
        <Div
            id="step"
            isContainer={true}
            paddingySM={1} paddingyMD={2} paddingyLG={3} paddingyXL={4}
        >
            <Div isRow>
                <Div cols={12}>
                    <H1 headingRef={setHeadingRef} className='ds-u-padding-bottom--3'>{header}</H1>
                    <Div isRow>
                        <Div colsMD={8}>
                            {showAlert && <Alert className='ds-u-margin-bottom--3'> 
                                {translate("enter_your_information_confirm")}
                            </Alert>}
                            {mfaPasswordReset && <Alert> 
                                {translate("create_new_password_protect")}
                            </Alert>}
                        </Div>
                    </Div>
                    {combinedWithUsername &&
                        <div className='ds-u-padding-bottom--3'>
                            <TranslatedSpan english={'get_username_and_reset_password'}/>
                        </div>
                    }
                    {showStepNum &&
                        <legend>
                            <Div paddingBottom={1}>
                                <StepHeader
                                    currentStep={step}
                                    numSteps={numSteps}
                                    stepTitle={stepTitle || ''}
                                />
                            </Div>
                        </legend>
                    }
                    {allFieldsRequired &&
                        <Div marginTop={3}>
                            <AllFieldsRequired />
                        </Div>
                    }
                </Div>
            </Div>
            {props.children}
        </Div>
    </>
};

interface ButtonProps {
    backText: string
    forwardText: string
    onBackClick: MouseEventHandler
    onForwardClick: MouseEventHandler
    isLoading: boolean
    testId?: string
}

export const Buttons = (props: ButtonProps) => {
    const {backText, forwardText, onBackClick, onForwardClick, isLoading} = props;

    const getBackButton = () => {
        return <>
            <Button
                onClick={onBackClick}
            >
                {backText}
            </Button>
        </>;
    };

    const getForwardButton = () => {
        return <>
            <Button
                onClick={onForwardClick}
                mfaAnalyticsData={{
                    text: forwardText,
                    parentComponentHeading: translate('reset_password')
                }}
                variation={'solid'}
                isLoading={isLoading}
            >
                {forwardText}
            </Button>
        </>;
    };

    return <>
        <StackingButtons
            button1={getBackButton()}
            button2={getForwardButton()}
        />
    </>;
};

interface ForgotPasswordProps {
    combinedWithUsername: boolean
    secretQuestion: string
    onBackClick: MouseEventHandler
    forgotUsernameData?: {
        dateOfBirth: DateObject
        lastName: string
        medicareNumber: string
        suffix: string
        username: string
    }
    testStep?: number | null
}

export type FieldErrState = 'empty' | 'invalid' | null;

interface LocationState {
    adminPasswordReset?: boolean, 
    mfaReset?: { 
        username: string, password: string 
    }
    loginUsername?: string
}

const ForgotPassword = (props: ForgotPasswordProps) => {
    const startingStep = props.combinedWithUsername ? 1 : 0;
    const question = props.secretQuestion ?? '';
    const location = useLocation();
    const { adminPasswordReset, mfaReset, loginUsername } = location.state as LocationState || { adminPasswordReset: null, mfaReset: null, loginUsername: ''};
    const [step, setStep] = useState(() => {
        if (props.testStep) {
            return props.testStep;
        }
        if (mfaReset && adminPasswordReset) {
            return 2;
        }
        return startingStep
    });
    const [username, setUsername] = useState(loginUsername || '');
    const [medicareNumber, setMedicareNumber] = useState('');
    const [lastName, setLastName] = useState('');
    const [suffix, setSuffix] = useState('');
    const [dateOfBirth, setDateOfBirth] = useState<DateObject>({day: '', month: '', year: ''});
    const [secretQuestion, setSecretQuestion] = useState(question);
    const [secretAnswer, setSecretAnswer] = useState('');

    const isFirstStep = step === 0;
    const formStart = useFormStart('reset_password', isFirstStep);
    const analyticsComponentName = props.combinedWithUsername ? 'forgot username and password' : 'reset_password';
    const conversion = useConversion(analyticsComponentName, 'reset_password');

    const history = useHistory();
    const goToLogin = () => history.push('/login');

    const enterYourInfo = <EnterYourInfo 
        key={step} 
        conversion={conversion} 
        setSecretQuestion={setSecretQuestion}
        formStart={formStart} 
        setStep={setStep} 
        forgotUsernameData={props.forgotUsernameData}
        username={username}
        setUsername={setUsername}
        medicareNumber={medicareNumber}
        setMedicareNumber={setMedicareNumber}
        lastName={lastName}
        setLastName={setLastName}
        suffix={suffix}
        setSuffix={setSuffix}
        dateOfBirth={dateOfBirth}
        setDateOfBirth={setDateOfBirth}
        enterInformationAlert={adminPasswordReset}
    />

    const answerSecretQuestion = <AnswerSecretQuestion 
        combinedWithUsername={props.combinedWithUsername}
        conversion={conversion}
        secretQuestion={secretQuestion}
        formStart={formStart}
        onBackClick={props.onBackClick}
        setStep={setStep}
        medicareNumber={medicareNumber}
        secretAnswer={secretAnswer}
        setSecretAnswer={setSecretAnswer}
        enterInformationAlert={adminPasswordReset}
    />

    const createNewPassword = <CreateNewPassword 
        combinedWithUsername={props.combinedWithUsername}
        conversion={conversion} 
        formStart={formStart}
        setStep={setStep}
        medicareNumber={medicareNumber}
        username={username}
        lastName={lastName}
        suffix={suffix}
        dateOfBirth={dateOfBirth}
        secretQuestion={secretQuestion}
        secretAnswer={secretAnswer}
        mfaReset={mfaReset}
        adminPasswordReset={adminPasswordReset}
    />

    const successHeader = translate('your_password_reset');

    const getTitle = () => {
        if (adminPasswordReset && mfaReset) {
            return translate("your_password_updated_header");
        }
        if (adminPasswordReset) {
            return translate("confirmed_identity");
        }
        return successHeader;
    }

    const getBody = () => {
        if (adminPasswordReset) {
            return <> <div className='ds-u-margin-bottom--1'>{translate("your_password_updated_body")}</div>
                {!mfaReset ? <div>{translate("set_up_next_time")}</div> : null } </>;
        }
        return translate("you_all_set_password_changed");
    }
    
    
    const success = <Step stepNum={4} title={getTitle()} combinedWithUsername={props.combinedWithUsername}>
        <Div isRow>
            <Div cols={12} colsMD={8} marginBottom={3}>
                <Alert variation="success">
                    {getBody()}
                </Alert>
            </Div>
        </Div>
        <Div
            isRow
            paddingBottom={3}
            style={{paddingLeft: 16}}>
            <Button variation="solid" mfaAnalyticsData={{
                text: translate('return_to_log_in'),
                parentComponentHeading: successHeader
            }} onClick={goToLogin}>
                <TranslatedSpan english={'return_to_log_in'}/>
            </Button>
        </Div>
    </Step>;
    
    const steps = [enterYourInfo, answerSecretQuestion, createNewPassword, success];

    return <>
        <fieldset style={{
            border: 0,
            margin: 0,
            padding: 0,
        }}>
            {steps[step]}
        </fieldset>
    </>;
};

export default ForgotPassword;
